import { BaseService } from '../../base';
import { firstEmailSegment } from '../../../shared/utils/first-email-segment.util';
import { firstNameSegment } from '../../../shared/utils/first-name-segment.util';
import { normalizeListSearch } from '../../../shared/utils/normalize-list-search.util';
import type {
  GetProposalsParams,
  InitiateProposalPayload,
  InitiateProposalResponse,
  ProposalApiItem,
  ProposalCategories,
  ProposalSummary,
  ProposalsApiResponse,
  ProposalsResponse,
  Schema,
  SendProposalEmailPayload,
} from '../types';

const DEFAULT_PAGE_SIZE = 5;
const PORTAL_API_URL = import.meta.env.VITE_API_URL as string | undefined;

function isGroupLifeProduct(productName?: string): boolean {
  if (!productName) return false;
  const lower = productName.toLowerCase();
  return lower.includes('vida em grupo') || lower.includes('pme');
}

function mapApiItemToSummary(item: ProposalApiItem): ProposalSummary {
  const id = item.policyId || item.id || '';
  const createdAt = item.proposalDate || '';

  const statusPortal = item.statusPortal ?? '';
  const proposalStatus =
    typeof statusPortal === 'string'
      ? statusPortal.trim()
      : String(statusPortal);

  // PME (Vida em Grupo) usa prêmio mensal; os demais produtos usam o valor
  // anual retornado diretamente pela API.
  const rawPremium = Number(item.duePremium) || 0;
  const duePremium = isGroupLifeProduct(item.productName)
    ? rawPremium / 12
    : rawPremium;

  return {
    id,
    proposerName: firstNameSegment(item.insuredName) || '-',
    proposerEmail: firstEmailSegment(item.insuredEmail) || '-',
    registerNumber: firstNameSegment(item.registerNumber) || '',
    brokerName: item.brokerName || '-',
    brokerDocument: item.brokerRegisterNumber || '',
    inclusionUserName:
      item.inclusionUserName?.trim() || item.insertByUserName?.trim() || '-',
    productName: item.productName || '-',
    productCode: item.productCode,
    proposalNumber:
      item.proposalNumber != null ? String(item.proposalNumber) : '-',
    createdAt,
    proposalStatus,
    inspectionStatus: null,
    duePremium,
  };
}

/**
 * Serviço com as chamadas universais do módulo de propostas — aquelas
 * expostas pelo `CommonProposalsController` no backend e que não dependem
 * do productCode (listagem, schema e envio de e-mail).
 *
 * Rotas específicas por produto (GET by policyId, calculate, attach-element,
 * pdf, etc.) moram em `HomeownerProposalsService` / `BusinessProposalsService`.
 */
export class CommonProposalsService extends BaseService {
  constructor() {
    super();
  }

  async initiateProposal(payload: InitiateProposalPayload): Promise<{
    success: boolean;
    data?: InitiateProposalResponse;
    error?: string;
    message?: string;
  }> {
    try {
      const path = '/portal/v1/proposals';
      const response = await this.post(path, payload, PORTAL_API_URL);
      return await this.handleJsonResponse<InitiateProposalResponse>(response);
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error ? error.message : 'Erro ao criar proposta',
      };
    }
  }

  async getProposals(params: GetProposalsParams = {}): Promise<{
    success: boolean;
    data?: ProposalsResponse;
    error?: string;
  }> {
    const {
      pageNumber = 1,
      pageSize = DEFAULT_PAGE_SIZE,
      search = '',
      sortField = 'index_time',
      sortType = 'DESC',
      status = '',
      partyCode,
      productCode,
      proposalDateFrom,
      proposalDateTo,
    } = params;
    const normalizedSearch = normalizeListSearch(search);

    try {
      const queryParams = new URLSearchParams({
        pageNumber: String(pageNumber),
        pageSize: String(pageSize),
        sortField,
        sortType,
      });

      if (normalizedSearch) queryParams.append('filter', normalizedSearch);
      if (status) queryParams.append('status', status);
      if (partyCode) queryParams.append('partyCode', partyCode);
      if (productCode) queryParams.append('productCode', productCode);
      if (proposalDateFrom)
        queryParams.append('proposalDateFrom', proposalDateFrom);
      if (proposalDateTo) queryParams.append('proposalDateTo', proposalDateTo);

      const path = `/portal/v1/proposals?${queryParams.toString()}`;
      const response = await this.get(path, {}, PORTAL_API_URL);
      const json =
        await this.handleJsonResponse<ProposalsApiResponse>(response);

      if (!json.success || !json.data) {
        return {
          success: false,
          error: json.error || 'Erro ao carregar propostas',
        };
      }

      return {
        success: true,
        data: {
          data: (json.data.data || []).map(mapApiItemToSummary),
          meta: json.data.meta,
        },
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erro de conexão',
      };
    }
  }

  async getSchema(category: ProposalCategories): Promise<{
    success: boolean;
    data?: Schema;
    error?: string;
  }> {
    try {
      const path = `/portal/v1/proposals/schema?category=${category}`;
      const response = await this.get(path, {}, PORTAL_API_URL);
      return this.handleJsonResponse<Schema>(response);
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error ? error.message : 'Erro ao carregar schema',
      };
    }
  }

  async cancelProposal(policyId: string): Promise<{
    success: boolean;
    data?: { policyId: string; statusPortal: string };
    error?: string;
  }> {
    try {
      const path = `/portal/v1/proposals/${encodeURIComponent(policyId)}/cancel`;
      const response = await this.post(path, {}, PORTAL_API_URL);
      return this.handleJsonResponse<{ policyId: string; statusPortal: string }>(
        response
      );
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error
            ? error.message
            : 'Erro ao cancelar a proposta',
      };
    }
  }

  async sendEmail(payload: SendProposalEmailPayload): Promise<{
    success: boolean;
    error?: string;
    message?: string;
  }> {
    // Endpoint unificado de envio de e-mail — o backend roteia o template
    // correto a partir do `productCode` enviado no payload.
    try {
      const path = '/portal/v1/proposals/send-email';
      const response = await this.post(path, payload, PORTAL_API_URL);
      return await this.handleJsonResponse<void>(response);
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erro ao enviar e-mail',
      };
    }
  }
}

export const commonProposalsService = new CommonProposalsService();
